import React, { useCallback, useEffect, useState } from 'react';
import { Newspaper, Calendar, ChevronRight, Loader2, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import SafeHtml from './SafeHtml';
import { triggerUnreadRefresh } from '../lib/unreadBus';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Full-page News feed — same data as the header bell, but a real page
 *  reachable from the main customer navigation. */
export default function NewsPage({ token }) {
  const { t } = useTranslation('common');
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/news/feed`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(r.ok ? await r.json() : []);
    } catch (_) { setItems([]); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openItem = async (item) => {
    setActive(item);
    if (item.is_read) return;
    try {
      await fetch(`${API_URL}/api/news/${item.id}/view`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, is_read: true } : i)));
      triggerUnreadRefresh();
    } catch (_) { /* silent */ }
  };

  const unread = items.filter((i) => !i.is_read).length;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="news-page">
      <div className="rounded-2xl bg-gradient-to-r from-cyan-600 to-blue-700 text-white p-5 sm:p-6 shadow-lg flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
          <Newspaper className="w-6 h-6" />
        </div>
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight">
            {t('news.title', { defaultValue: 'News & Updates' })}
          </h1>
          <p className="text-white/85 text-sm mt-0.5">
            {t('news.page_subtitle', { defaultValue: 'Service announcements, new features and price updates.' })}
            {unread > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-red-500 text-white text-[11px] font-bold" data-testid="news-page-unread">
                {unread} {t('news.unread', { defaultValue: 'unread' })}
              </span>
            )}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex items-center justify-center text-slate-500" data-testid="news-page-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('news.loading', { defaultValue: 'Loading news…' })}
        </div>
      ) : active ? (
        <div className="mt-5 bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-8" data-testid="news-page-detail">
          <button
            onClick={() => setActive(null)}
            data-testid="news-page-back"
            className="text-sm text-cyan-700 hover:text-cyan-800 font-medium inline-flex items-center gap-1.5 mb-5"
          >
            <ArrowLeft className="w-4 h-4" /> {t('news.back', { defaultValue: 'Back to all news' })}
          </button>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">{active.title}</h2>
          <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-2 mb-6 pb-4 border-b border-slate-100">
            <Calendar className="w-4 h-4" /> {fmt(active.created_at)}
          </p>
          <SafeHtml as="div" className="news-body" html={active.body_html} />
        </div>
      ) : items.length === 0 ? (
        <div className="py-20 flex flex-col items-center text-slate-400" data-testid="news-page-empty">
          <Newspaper className="w-14 h-14 mb-3 opacity-40" />
          <p className="text-base">{t('news.empty', { defaultValue: 'No news at the moment. Check back later!' })}</p>
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => openItem(n)}
              data-testid={`news-page-item-${n.id}`}
              className={`group text-left rounded-xl border-2 p-4 sm:p-5 transition-all hover:shadow-md hover:-translate-y-0.5 ${
                n.is_read ? 'bg-white border-slate-200' : 'bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200'
              }`}
            >
              <div className="flex items-start gap-2 mb-2">
                {!n.is_read && <span className="mt-1.5 flex-shrink-0 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />}
                <h3 className={`flex-1 ${n.is_read ? 'font-semibold text-slate-700' : 'font-bold text-slate-900'} text-base sm:text-lg leading-snug`}>
                  {n.title}
                </h3>
                <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
              </div>
              <p className="text-xs text-slate-500 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" /> {fmt(n.created_at)}
                {!n.is_read && (
                  <span className="ml-auto text-[10px] font-bold uppercase tracking-wider bg-red-500 text-white px-2 py-0.5 rounded-full">
                    {t('news.new', { defaultValue: 'New' })}
                  </span>
                )}
              </p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
